import { Component, computed, inject, Signal, signal } from '@angular/core';
import { form, FormField, required, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { ThumbnailsCarouselComponent } from '@app/shared/components/carousel/thumbnails-carousel/thumbnails-carousel.component';
import { FileDropComponent } from '@app/shared/components/files/file-drop/file-drop.component';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { FileDataService } from '@app/shared/services/file-data.service';
import { LocationsActionsService } from '@app/shared/services/locations-actions.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { FileContext } from '@app/shared/types/enums/file-enums';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import {
  Location,
  LocationNameFormModel,
  ProjectLocationEditDialogData
} from '@app/shared/types/models/location/location.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'proffeo-edit-location-dialog',
  imports: [
    MatDialogModule,
    TranslatePipe,
    UiDialogComponent,
    FormField,
    FileDropComponent,
    UiButtonComponent,
    ThumbnailsCarouselComponent,
    UiInputComponent
  ],
  templateUrl: './edit-location-dialog.component.html'
})
export class EditLocationDialogComponent {
  private readonly dialogRef: MatDialogRef<EditLocationDialogComponent> = inject(
    MatDialogRef<EditLocationDialogComponent>
  );
  private readonly fileService: FileDataService = inject(FileDataService);
  private readonly locationsDataService: LocationsDataService = inject(LocationsDataService);
  private readonly dialogData: ProjectLocationEditDialogData = inject(MAT_DIALOG_DATA);
  private readonly locationActionsService: LocationsActionsService = inject(LocationsActionsService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);

  protected readonly location: Location = this.dialogData.projectLocationToEdit;
  protected readonly model = signal<LocationNameFormModel>({ name: this.location.name ?? '' });
  protected readonly form = form(this.model, path => {
    required(path.name, { message: 'ui-forms-this-field-is-required' });
  });
  protected locationFiles = computed(() =>
    this.fileService.getFiles({
      context: FileContext.locationFile,
      projectId: this.location.projectId,
      locationId: this.location.id
    })()
  );
  protected selectedProject: Signal<Project> = this.activeProjectService.activeProject;

  public save(): void {
    void submit(this.form, {
      action: async () => {
        try {
          await firstValueFrom(
            this.locationsDataService.updateLocation(
              this.selectedProject().id,
              this.location.id,
              this.model().name,
              this.location.orderNo,
              this.location?.parentLocation()?.id
            )
          );
          this.dialogRef.close();
        } catch {
          // HTTP errors are reported globally; the dialog stays open for a retry.
        }
      }
    });
  }

  protected onFileAdded(files: File[]): void {
    this.locationActionsService.addFiles(
      computed(() => this.location),
      files,
      this.locationFiles().length
    );
  }

  protected onThumbnailClick(index: number): void {
    this.locationActionsService.openCarouselWithLocationFiles(
      computed(() => this.location),
      this.locationFiles,
      index
    );
  }

  protected onFileDelete(file: FileInfo): void {
    this.locationActionsService.deleteFile(
      computed(() => this.location),
      file
    );
  }
}
